import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const RolePermissionMatrix = ({ roles, permissions, rolePermissions, onUpdatePermissions, currentUserRole = 'admin' }) => {
  const [expandedRoles, setExpandedRoles] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [localPermissions, setLocalPermissions] = useState(rolePermissions);
  const [saveError, setSaveError] = useState(null);

  // Role-based access verification
  const canManagePermissions = () => {
    return currentUserRole === 'admin';
  };

  const validatePermissionChanges = () => {
    // Ensure admin role always has all permissions
    const adminPerms = localPermissions?.admin;
    if (adminPerms) {
      const hasAllPermissions = permissions?.every(p => adminPerms?.[p?.id] === true);
      if (!hasAllPermissions) {
        return 'يجب أن يحتفظ مدير النظام بجميع الصلاحيات';
      }
    }

    // Ensure at least one role has manage_users permission
    const hasUserManager = Object.values(localPermissions)?.some(
      rolePerms => rolePerms?.manage_users === true
    );
    if (!hasUserManager) {
      return 'يجب أن يكون لدور واحد على الأقل صلاحية إدارة المستخدمين';
    }

    return null;
  };

  const toggleRole = (roleId) => {
    setExpandedRoles(prev =>
      prev?.includes(roleId)
        ? prev?.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const handlePermissionChange = (roleId, permissionId, checked) => {
    if (!canManagePermissions()) {
      setSaveError('ليس لديك صلاحية لتعديل الصلاحيات');
      return;
    }

    setLocalPermissions(prev => ({
      ...prev,
      [roleId]: {
        ...prev?.[roleId],
        [permissionId]: checked
      }
    }));
    setHasChanges(true);
    setSaveError(null);
  };

  const handleSelectAll = (roleId, checked) => {
    if (!canManagePermissions()) {
      setSaveError('ليس لديك صلاحية لتعديل الصلاحيات');
      return;
    }

    const updatedPermissions = {};
    permissions?.forEach(permission => {
      updatedPermissions[permission.id] = checked;
    });
    setLocalPermissions(prev => ({
      ...prev,
      [roleId]: updatedPermissions
    }));
    setHasChanges(true);
    setSaveError(null);
  };

  const handleSave = () => {
    if (!canManagePermissions()) {
      setSaveError('ليس لديك صلاحية لحفظ التغييرات');
      return;
    }

    const validationError = validatePermissionChanges();
    if (validationError) {
      setSaveError(validationError);
      return;
    }

    onUpdatePermissions(localPermissions);
    setHasChanges(false);
    setSaveError(null);
  };

  const handleReset = () => {
    setLocalPermissions(rolePermissions);
    setHasChanges(false);
  };

  return (
    <div className="bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] shadow-warm">
      <div className="p-4 md:p-6 border-b border-[var(--color-border)] flex items-center justify-between gap-4">
        <div>
          <h3 className="text-lg md:text-xl font-heading font-semibold text-[var(--color-foreground)]">
            مصفوفة الصلاحيات
          </h3>
          <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
            إدارة صلاحيات الوصول لكل دور وظيفي
          </p>
          {!canManagePermissions() && (
            <p className="text-xs text-[var(--color-warning)] mt-2 flex items-center gap-1">
              <Icon name="AlertCircle" size={14} />
              وضع القراءة فقط - ليس لديك صلاحية التعديل
            </p>
          )}
        </div>
        {hasChanges && canManagePermissions() && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleReset}>
              إلغاء
            </Button>
            <Button variant="default" size="sm" iconName="Save" onClick={handleSave}>
              حفظ التغييرات
            </Button>
          </div>
        )}
      </div>
      {saveError && (
        <div className="p-4 bg-[var(--color-error)]/10 border-b border-[var(--color-error)]/20">
          <div className="flex items-center gap-2 text-[var(--color-error)]">
            <Icon name="AlertTriangle" size={20} />
            <span className="text-sm font-medium">{saveError}</span>
          </div>
        </div>
      )}
      <div className="p-4 md:p-6 space-y-4">
        {roles?.map((role) => (
          <div key={role?.id} className="border border-[var(--color-border)] rounded-lg overflow-hidden">
            <button
              onClick={() => toggleRole(role?.id)}
              className="w-full p-4 flex items-center justify-between bg-[var(--color-muted)] hover:bg-[var(--color-muted)]/80 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Icon
                  name={expandedRoles?.includes(role?.id) ? 'ChevronDown' : 'ChevronLeft'}
                  size={20}
                  color="var(--color-foreground)"
                />
                <div className="text-right">
                  <h4 className="text-base font-semibold text-[var(--color-foreground)]">{role?.label}</h4>
                  <p className="text-sm text-[var(--color-muted-foreground)]">{role?.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--color-muted-foreground)]">
                  {Object.values(localPermissions?.[role?.id] || {})?.filter(Boolean)?.length} / {permissions?.length}
                </span>
                {canManagePermissions() && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleSelectAll(role?.id, true);
                    }}
                  >
                    تحديد الكل
                  </Button>
                )}
              </div>
            </button>

            {expandedRoles?.includes(role?.id) && (
              <div className="p-4 bg-[var(--color-background)] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {permissions?.map((permission) => (
                  <div key={permission?.id} className="flex items-start gap-3 p-3 bg-[var(--color-card)] rounded-lg border border-[var(--color-border)]">
                    <Checkbox
                      checked={localPermissions?.[role?.id]?.[permission?.id] || false}
                      onChange={(e) => handlePermissionChange(role?.id, permission?.id, e?.target?.checked)}
                      disabled={!canManagePermissions()}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[var(--color-foreground)]">{permission?.label}</p>
                      <p className="text-xs text-[var(--color-muted-foreground)] mt-1">{permission?.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RolePermissionMatrix;